import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box } from 'rebass';
import useOnClickOutside from 'use-onclickoutside';
import {
  getBoundingBox,
  clampTranslate,
  clampResize,
  translateLayers,
  scaleLayersToBox,
} from '../hooks/layer-geometry';

const HANDLE_SIZE = 90;
const HANDLE_BORDER_WIDTH = 10;
const HANDLES = [
  { key: 'nw', top: 0, left: 0, cursor: 'nwse-resize' },
  { key: 'ne', top: 0, left: '100%', cursor: 'nesw-resize' },
  { key: 'sw', top: '100%', left: 0, cursor: 'nesw-resize' },
  { key: 'se', top: '100%', left: '100%', cursor: 'nwse-resize' },
];

// "Blue highlight ... colour: #4AA9FF at 40% opacity" (hover). Selected state
// uses a solid border instead, per the design's SelectedState panel.
const HOVER_FILL = 'rgba(74, 169, 255, 0.4)';
const SELECTED_BORDER = '#4AA9FF';

// One of these is rendered per media group (see layer-geometry's
// groupLayers) — hover highlight when idle, a draggable/resizable bounding
// box with corner handles once selected. Coordinates are in the page's
// native unit space (same as xpos/ypos/width/height); the ambient
// `transform: scale()` on the page container handles the on-screen zoom, so
// only pointer *deltas* need converting back from screen px via `zoomValue`.
const MediaSelectionOverlay = ({ group, isSelected, isLocked, layerEditor }) => {
  const { getRect, zoomValue, pageWidth, pageHeight, onSelectLayer, onBeginInteraction, onApplyLive, onEndInteraction, onCancelInteraction, layers } = layerEditor;
  const dragRef = useRef(null);
  const overlayRef = useRef(null);

  // A click anywhere outside this group's own DOM (including its resize
  // handles, so a drag/resize gesture's trailing click never trips this) —
  // blank page area, another media group, the sidebar, the dark background,
  // anywhere — deselects it, so there's no gap where clicking just outside
  // the group's edge fails to register as "outside" and leaves it selected.
  //
  // EXCEPT the toolbars that act ON the current selection (Crop Photo,
  // Undo/Redo/Save Changes) — this fires on `mousedown`, which happens
  // before their own `click` handler, so deselecting first would make
  // Crop Photo find nothing selected and silently do nothing.
  useOnClickOutside(overlayRef, event => {
    if (!isSelected || isLocked) return;
    if (event.target.closest?.('[data-preserve-selection]')) return;
    onSelectLayer(null);
  });

  const canUserSelect = layers?.length && group?.layerIds.length > 0 &&
    group?.layerIds.some(selectedId => {
      const layer = layers.find(l => l.id === selectedId);
      return (layer && layer.kind === 'user');
    });

  // QR codes stop scanning once their aspect ratio is distorted. The group
  // itself still resizes — other members scale normally — a QR code layer
  // just never has its own rect touched by that scaling (see handlePointerMove).
  const isQrCodeLayer = useCallback(id => {
    const layer = layers?.find(l => l.id === id);
    return typeof layer?.['component-value'] === 'string' && layer['component-value'].includes('qr_code');
  }, [layers]);

  // The two QR variants anchor to the photo's bottom-right corner
  // differently: the rounded-corners badge sits flush against it, while the
  // plain square QR is inset 30px up and to the left of it.
  const getQrOffset = useCallback(id => {
    const layer = layers?.find(l => l.id === id);
    return layer?.['component-value'] === 'qr_code' ? 30 : 0;
  }, [layers]);

  // A QR code always belongs to exactly one photo in its group and must stay
  // pinned to that photo's bottom-right corner — prefer the group's actual
  // image layer; fall back to "whichever member isn't the QR" if the group
  // doesn't carry an explicit image-typed layer for some reason.
  const findPhotoLayerId = useCallback(() => {
    const imageLayer = group.layerIds.find(id => {
      const layer = layers?.find(l => l.id === id);
      return layer && layer.type === 'Books::PageImageLayer' && !isQrCodeLayer(id);
    });
    if (imageLayer) return imageLayer;
    return group.layerIds.find(id => !isQrCodeLayer(id));
  }, [group.layerIds, layers, isQrCodeLayer]);

  const startDrag = useCallback((mode, handle, e) => {
    if (isLocked) return;
    e.stopPropagation();
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);

    const startRects = {};
    group.layerIds.forEach(id => { startRects[id] = getRect(id); });
    const startBox = getBoundingBox(Object.values(startRects));

    dragRef.current = { mode, handle, startX: e.clientX, startY: e.clientY, startRects, startBox };
    onBeginInteraction();
  }, [group.layerIds, getRect, isLocked, onBeginInteraction]);

  const handleMovePointerDown = useCallback(e => startDrag('move', null, e), [startDrag]);
  const handleResizePointerDown = handle => e => startDrag('resize', handle, e);

  const handlePointerMove = useCallback(e => {
    const drag = dragRef.current;
    if (!drag) return;
    const scale = (zoomValue || 100) / 100;
    const dxNative = (e.clientX - drag.startX) / scale;
    const dyNative = (e.clientY - drag.startY) / scale;

    if (drag.mode === 'move') {
      const { dx, dy } = clampTranslate(drag.startBox, dxNative, dyNative, pageWidth, pageHeight);
      onApplyLive(translateLayers(drag.startRects, dx, dy));
    } else {
      const nextBox = clampResize(drag.startBox, drag.handle, dxNative, dyNative, pageWidth, pageHeight);
      const scaled = scaleLayersToBox(drag.startRects, drag.startBox, nextBox);

      // A QR code in the group keeps its original width/height — scanning
      // breaks once its aspect ratio is distorted — and is re-pinned to the
      // bottom-right corner of its group's photo layer using that photo's
      // JUST-RESIZED rect, rather than letting it drift to wherever
      // proportional group scaling would otherwise put it.
      const photoLayerId = findPhotoLayerId();
      const photoRect = photoLayerId ? (scaled[photoLayerId] || drag.startRects[photoLayerId]) : null;

      Object.keys(scaled).forEach(id => {
        if (!isQrCodeLayer(id)) return;
        const qrSize = drag.startRects[id];
        const offset = getQrOffset(id);
        scaled[id] = photoRect
          ? {
            xpos: photoRect.xpos + photoRect.width - qrSize.width - offset,
            ypos: photoRect.ypos + photoRect.height - qrSize.height - offset,
            width: qrSize.width,
            height: qrSize.height,
          }
          : { ...scaled[id], width: qrSize.width, height: qrSize.height };
      });

      onApplyLive(scaled);
    }
  }, [zoomValue, pageWidth, pageHeight, onApplyLive, isQrCodeLayer, findPhotoLayerId, getQrOffset]);

  const handlePointerUp = useCallback(e => {
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (!dragRef.current) return;
    dragRef.current = null;
    onEndInteraction();
  }, [onEndInteraction]);

  const handlePointerCancel = useCallback(e => {
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (!dragRef.current) return;
    dragRef.current = null;
    onCancelInteraction();
  }, [onCancelInteraction]);

  // Always stop propagation, even once already selected — a pointerdown +
  // pointermove + pointerup drag gesture still ends in a browser-synthesized
  // click on pointer-up. While selected this element has no onClick handler
  // wired for that click to be consumed by, so without this it bubbles to
  // the page's own onClick (clears the selection and resets `currentPage`)
  // the instant a drag or resize finishes — which is exactly the bug where
  // the selection/handles vanish right after moving or resizing an element.
  const handleOverlayClick = useCallback(e => {
    e.stopPropagation();
    if (isLocked || isSelected || !canUserSelect) return;
    onSelectLayer({ id: group.layerIds[0] });
  }, [isLocked, isSelected, onSelectLayer, group.layerIds]);

  const box = getBoundingBox(group.layerIds.map(id => getRect(id)));

  if (!box) return null;

  return (
    <Box
      ref={overlayRef}
      data-testid={`media-selection-${group.key}`}
      onClick={handleOverlayClick}
      onPointerDown={isSelected && canUserSelect ? handleMovePointerDown : undefined}
      onPointerMove={isSelected && canUserSelect ? handlePointerMove : undefined}
      onPointerUp={isSelected && canUserSelect ? handlePointerUp : undefined}
      onPointerCancel={isSelected && canUserSelect ? handlePointerCancel : undefined}
      sx={{
        position: 'absolute',
        left: `${box.xpos}px`,
        top: `${box.ypos}px`,
        width: `${box.width}px`,
        height: `${box.height}px`,
        zIndex: 900,
        boxSizing: 'border-box',
        border: isSelected ? `20px solid ${SELECTED_BORDER}` : '3px solid transparent',
        background: isSelected ? 'transparent' : 'transparent',
        cursor: (isLocked || !canUserSelect) ? 'default' : isSelected ? 'move' : 'pointer',
        pointerEvents: isLocked ? 'none' : 'auto',
        touchAction: isSelected ? 'none' : 'auto',
        '&:hover': !isSelected && !isLocked && canUserSelect ? { background: HOVER_FILL } : undefined,
      }}
    >
      {isSelected && !isLocked && canUserSelect && HANDLES.map(handle => (
        <Box
          key={handle.key}
          data-testid={`resize-handle-${group.key}-${handle.key}`}
          onPointerDown={handleResizePointerDown(handle.key)}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onClick={e => e.stopPropagation()}
          sx={{
            position: 'absolute',
            top: handle.top,
            left: handle.left,
            width: `${HANDLE_SIZE}px`,
            height: `${HANDLE_SIZE}px`,
            marginTop: `-${HANDLE_SIZE / 2}px`,
            marginLeft: `-${HANDLE_SIZE / 2}px`,
            borderRadius: '50%',
            bg: '#fff',
            border: `${HANDLE_BORDER_WIDTH}px solid ${SELECTED_BORDER}`,
            cursor: handle.cursor,
            zIndex: 901,
          }}
        />
      ))}
    </Box>
  );
};

MediaSelectionOverlay.propTypes = {
  group: PropTypes.shape({
    key: PropTypes.string.isRequired,
    layerIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  isLocked: PropTypes.bool,
  layerEditor: PropTypes.shape({
    getRect: PropTypes.func.isRequired,
    zoomValue: PropTypes.number,
    pageWidth: PropTypes.number,
    pageHeight: PropTypes.number,
    layers: PropTypes.array,
    onSelectLayer: PropTypes.func.isRequired,
    onBeginInteraction: PropTypes.func.isRequired,
    onApplyLive: PropTypes.func.isRequired,
    onEndInteraction: PropTypes.func.isRequired,
    onCancelInteraction: PropTypes.func.isRequired,
  }).isRequired,
};

MediaSelectionOverlay.defaultProps = {
  isLocked: false,
};

export default React.memo(MediaSelectionOverlay);
